import React, { memo, useCallback } from 'react'
import { ControlledCheckbox, ControlledTextInput } from 'transfix-form/components'
import { FormModal, IFormModalProps } from 'transfix-ui/components/Modals/FormModal'
import Box from 'transfix-ui/core/Box'
import Grid from 'transfix-ui/core/Grid'
import Typography from 'transfix-ui/core/Typography'
import { TCarrierFleetInformationSubmitHandler } from '../../types'
import { IUseEquipmentTypesProps, useEquipmentTypesForm } from './hooks/useEquipmentTypesForm'
import { TEquipmentTypesSchema } from './schema/EquipmentTypesSchema'

export interface IEquipmentTypesModalProps extends Pick<IFormModalProps, 'onClose'> {
  /**
   * @default {}
   */
  defaultValues?: IUseEquipmentTypesProps['defaultValues']
  loading: boolean
  onSumbit: TCarrierFleetInformationSubmitHandler<TEquipmentTypesSchema>
}

const EquipmentTypesModal = memo<IEquipmentTypesModalProps>(
  ({ defaultValues = {}, loading, onClose, onSumbit }) => {
    const { control, handleSubmit } = useEquipmentTypesForm({ defaultValues })

    const submitHandler = useCallback(
      async (formValues: TEquipmentTypesSchema) => {
        const success = await onSumbit(formValues)
        if (success) {
          onClose()
        }
      },
      [onClose, onSumbit]
    )

    const actions: IFormModalProps['actions'] = [
      {
        label: 'Cancel',
        type: 'dismiss',
        action: onClose,
        disabled: loading,
      },
      {
        label: 'Submit',
        type: 'confirm',
        action: handleSubmit(submitHandler),
        disabled: loading,
      },
    ]

    return (
      <FormModal title='Equipment types' actions={actions} size={5} onClose={onClose} open>
        <Grid container>
          <Grid item xs={12}>
            <Box mb={1.5}>
              <Typography component='div'>
                Confirm your power units, trailers, and equipment types
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box mb={2}>
              <ControlledTextInput
                control={control}
                name='powerUnitCount'
                TextInputProps={{
                  label: 'Power Units',
                  type: 'number',
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box mb={2}>
              <ControlledTextInput
                control={control}
                name='trailerCount'
                TextInputProps={{
                  label: 'Trailers',
                  type: 'number',
                }}
              />
            </Box>
          </Grid>
          <Grid item md={6} xs={12}>
            <ControlledCheckbox
              control={control}
              name='isDryVanCapable'
              CheckboxProps={{ label: 'Dry Van' }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <ControlledCheckbox
              control={control}
              name='isFlatbedCapable'
              CheckboxProps={{ label: 'Flatbed/Stepback' }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <ControlledCheckbox
              control={control}
              name='isReeferCapable'
              CheckboxProps={{ label: 'Reefer' }}
            />
          </Grid>
        </Grid>
      </FormModal>
    )
  }
)

export default EquipmentTypesModal
